import { useContext } from 'react'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useRelatedEntity, { useRelatedEntityForm, useRelations } from '@hooks/useRelatedEntity'

import { entitySettings, templatePageTypes, userProjectRoles } from '@functions/relatedEntity'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import EntitySelector from '@components/EntitySelector'

const MODAL_KEY = 'CreateOrEditRelatedEntityModal'

type ModalCallbacks = {
  closeModal: () => void,
  createRelatedEntity: (payload: HandleCallbackActionParams) => void,
  updateRelatedEntity: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditRelatedEntityModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, createRelatedEntity, updateRelatedEntity } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const initRelatedEntity = digObject(modalPayload, 'relatedEntity', {})

  const newRecord = !initRelatedEntity.id

  const {
    callbacks: {
      createRelatedEntity: createFn,
      updateRelatedEntity: updateFn,
    },
    creating,
    relatedEntity,
    updating,
  } = useRelatedEntity(initRelatedEntity)

  const {
    entityState,
    entityState: {
      data,
      service_product_id,
      subject_type,
      subject_id,
    },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = useRelatedEntityForm(relatedEntity, { validateOn: 'change' })

  const { serviceProductTemplatePages } = useRelations(entityState)

  // To be used with handleCallbackAction
  const createOrUpdateRelatedEntityPayload = {
    callbacks: {
      action: newRecord ? createFn : updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `Connection ${newRecord ? 'Created' : 'Updated'}`,
  }

  const selectedSubjectSettings = entitySettings[subject_type]

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title={newRecord ? 'Create' : 'Edit'} titleSecondLine="Connection" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Subject Type" errorMessage={errors.subject_type}>
            <select name="subject_type" value={subject_type} {...handlers}>
              <option value="">Please Select...</option>
              {Object.keys(entitySettings).map((subjectSettingKey: string) => {
                const { label } = entitySettings[subjectSettingKey]

                return (
                  <option key={subjectSettingKey} value={subjectSettingKey}>
                    {label}
                  </option>
                )
              })}
            </select>
          </Form.Field>

          {subject_type === 'TemplatePage' && (
            <Form.Field label="* Template Type" marginTop="large">
              <select name="data.template_type" value={data?.template_type} {...handlers}>
                <option value="">Please Select...</option>
                {Object.keys(templatePageTypes).map((templatePageType: string) => {
                  const label = templatePageTypes[templatePageType]

                  return (
                    <option key={templatePageType} value={templatePageType}>
                      {label}
                    </option>
                  )
                })}
              </select>
            </Form.Field>
          )}

          {subject_type === 'User' && (
            <Form.Field label="* User Role" marginTop="large">
              <select name="data.role" value={data?.role} {...handlers}>
                <option value="">Please Select...</option>
                {Object.keys(userProjectRoles).map((userProjectRole: string) => {
                  const label = userProjectRoles[userProjectRole]

                  return (
                    <option key={userProjectRole} value={userProjectRole}>
                      {label}
                    </option>
                  )
                })}
              </select>
            </Form.Field>
          )}

          {selectedSubjectSettings && (
            <EntitySelector
              callbacks={{
                getSearchEntities: entities => entities.map((entity) => {
                  const { id, title } = entity
                  return { ...entity, title: `#${id} - ${title}` }
                }),
                selectItem: entity => setEntityState({
                  data: { ...data, template_page_type: undefined },
                  service_product_id: subject_type === 'ServiceProduct' ? entity?.id : service_product_id,
                  subject_id: entity?.id,
                }),
              }}
              label={`* ${selectedSubjectSettings.label}`}
              entityKey={selectedSubjectSettings.entityKey}
              marginTop="large"
              selectedItemId={subject_id}
              showEntityId
            />
          )}

          {!!subject_id && !!serviceProductTemplatePages.length && (
            <Form.Field label="* Template Page Type" marginTop="large">
              <select name="data.template_page_type" value={data?.template_page_type} {...handlers}>
                <option value="">Please Select...</option>
                {serviceProductTemplatePages.map((page: { title: string }) => {
                  const { title: pageTitle } = page

                  return (
                    <option key={pageTitle} value={pageTitle}>
                      {pageTitle}
                    </option>
                  )
                })}
              </select>
            </Form.Field>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={
            newRecord
              ? () => createRelatedEntity(createOrUpdateRelatedEntityPayload)
              : () => updateRelatedEntity(createOrUpdateRelatedEntityPayload)
          }
          size="large"
        >
          {saveEnabled ? `${newRecord ? 'Create' : 'Update'} Connection` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditRelatedEntityModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
